import { useEffect, useLayoutEffect, useState } from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CAFETERIA_MODEL } from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import {GetAvailableDevicesByModeIds} from "../../../../../reduxToolkit/FoodieCafeteria/CafeteriaApi";
import { GetListStatus } from "../../../../../reduxToolkit/Commons/CommonsApi";
import {DAYS_MODEL, deviceListToLabel} from "../../../../../Apis/functions";
import {MODE_MODEL} from "../../../Devices/Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Main hook managing cafeteria form logic
const CommonCafeteriaApp = () => {
    // Extract cafeteria ID from URL params
    const location = useLocation(); // React Router hook for accessing current route
    const state = location.state;   // Access route state (e.g. ID passed from previous page)
    const id = state && state.state; // Cafeteria ID for editing
    const { t } = useTranslation(); // For internationalization
    const dispatch = useDispatch(); // Redux dispatcher
    const navigate = useNavigate(); // For navigation
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development mode from Redux
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions
    const { getListStatus } = useSelector(state => state.CommonsSlice); // List of cafeteria statuses

    // Local state variables
    const [disabled, setDisabled] = useState(false); // Form disabled status
    const [emptyFields, setEmptyFields] = useState(false); // Indicates if required fields are empty
    const [activeStep, setActiveStep] = useState(0); // Wizard step
    const [steps, setSteps] = useState([]); // Wizard steps (titles)
    const [item, setItem] = useState({ ...CAFETERIA_MODEL }); // Cafeteria data
    const { handleChange } = HandleChange({ model: item, setModel: setItem }); // Generic input change handler
    const [dayArray, setDayArray] = useState([]); // Selected days for cafeteria schedule
    const [deviceList, setDeviceList] = useState([]); // List of devices from API
    const [deviceSelectedList, setDeviceSelectedList] = useState([]); // Selected devices
    const [showOverlay, setShowOverlay] = useState(false); // Overlay visibility state
    const [firstClickOnMap, setFirstClickOnMap] = useState(true); // Flag for map click initialization
    const [isValid, setIsValid] = useState(true); // Capacity field validation
    const [isValidCloseTime, setIsValidCloseTime] = useState(true);

    /**
     * Assign translated names for each wizard step.
     */
    useLayoutEffect(() => {
        setSteps([t('set_up'), t("location"), t("diner_access"), t("summary")]);
    }, [permission, t]);

    /**
     * Fetch the cafeteria status list and available devices on mount.
     */
    useEffect(() => {
        dispatch(GetListStatus()); // Fetch status list
        dispatch(GetAvailableDevicesByModeIds({foodieCafeteriaId: id, modeIds: [MODE_MODEL.FOODIE_ACCESS, MODE_MODEL.FOODIE_KIOSK, MODE_MODEL.FOODIE_CHECK_IN, MODE_MODEL.FOODIE_CHECK_OUT] })).then(({ payload: { data: { data } } }) => {
            // Map device data to dropdown options
            let aux = [];
            if(data?.length > 0)
                aux = deviceListToLabel(data)

            setDeviceList(aux);
        });
    }, []);

    /**
     * Handle changing the active wizard step.
     * @param step - The new active step index
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Handle form field changes with validation logic.
     * @param evt - The change event
     */
    const HandleChangeControl = (evt) => {
        // Validate capacity field
        if (evt?.target?.name === "capacity") {
            if (parseInt(evt?.target?.value) > 0) setIsValid(true);
            else setIsValid(false);
        }

        // Reset map click flag for latitude/longitude changes
        if (evt?.target?.name === "latitude" || evt?.target?.name === "longitude") {
            setFirstClickOnMap(true);
        }

        handleChange(evt); // Delegate to generic handler
    };

    /**
     * Converts selected days (dayArray) into the cafeteria data structure.
     * @param data - The cafeteria object
     * @returns data - Updated cafeteria object with schedule days
     */
    const toRegisterDays = (data) => {
        DAYS_MODEL.map(e => {
            data.schedule[e.key] = dayArray.includes(e.value);
        });
        return data;
    };

    /**
     * Validates the cafeteria closing time whenever the opening or closing time changes.
     *
     * This effect compares `openTime` and `closeTime` from the `item` object.
     * If both values are present and the closing time is greater than the opening time,
     * the close time is considered valid and `isValidCloseTime` is set to `true`.
     * Otherwise, the close time is marked as invalid.
     */
    useEffect(() => {
        if(item.openTime && item.closeTime)
            if(item.closeTime > item.openTime) setIsValidCloseTime(true);
            else setIsValidCloseTime(false);
    }, [item.openTime, item.closeTime]);

    // Expose necessary state and handlers
    return {
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        id,
        item,
        setItem,
        getListStatus,
        disabled,
        emptyFields,
        setEmptyFields,
        activeStep,
        setActiveStep,
        steps,
        setSteps,
        dayArray,
        setDayArray,
        deviceList,
        setDeviceList,
        deviceSelectedList,
        setDeviceSelectedList,
        showOverlay,
        setShowOverlay,
        isValid,
        setIsValid,
        firstClickOnMap,
        setFirstClickOnMap,
        handleStepChange,
        toRegisterDays,
        handleChange: HandleChangeControl,
        isValidCloseTime,
        setIsValidCloseTime
    };
};

export default CommonCafeteriaApp;
